/**
 * SampleReadAlongNavigator
 *
 * A slim, floating "you're here, next is X" pill anchored in the
 * bottom-right of the viewport. Mounted exclusively on
 * `/sample-analysis` so it doesn't collide with the in-report TOC
 * sidebar shown for paid reports.
 *
 * Two states:
 *   1. NAV chip (default): "Section X of N · Up next: {next} →"
 *      Clicking it scrolls to the next section.
 *   2. UPLOAD chip (after ~70% of sections consumed): "You've read most
 *      of the sample · Upload yours →" — routes to /upload.
 *
 * Hides:
 *   - On the very last section (no "up next")
 *   - When fewer than 3 sections exist
 *   - Before the user has scrolled past the first section
 *
 * Telemetry (consent-gated via lib/analytics):
 *   - sample_navigator_shown { surface: "sample_analysis" }
 *   - sample_navigator_section_clicked { from, to, from_index, to_index }
 *   - sample_navigator_upload_cta_clicked { from, from_index, sections_total }
 *
 * Reuses the same `data-toc-section` attribute the report already
 * exposes for the desktop TOC sidebar, so no markup changes to
 * ReportFull are required.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen, FileUp, X } from "lucide-react";
import { track } from "@/lib/analytics";

// Same canonical mapping the report TOC uses. Anything not listed here
// falls back to the raw id (Sentence-cased) so the navigator still works
// if a new section is added before this list is updated.
const SECTION_LABELS = {
    summary: "Summary",
    "top-priorities": "Top Priorities",
    readiness: "Readiness",
    "deal-risk": "Deal Risk",
    exposure: "Repair Exposure",
    controls: "Filters",
    issues: "Issues",
    "not-panic": "Calm Context",
    premium: "Negotiation",
    specialists: "Specialists",
    questions: "Questions",
    quotes: "Quotes",
};

function prettyLabel(id) {
    if (SECTION_LABELS[id]) return SECTION_LABELS[id];
    return id
        .split("-")
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(" ");
}

// Threshold: once the user is past this fraction of sections, the chip
// pivots from "what's next?" to a contextual upload CTA.
const UPLOAD_CTA_THRESHOLD = 0.7;

// Don't show the chip until the user has scrolled at least this far
// (avoids appearing immediately on page load before the visitor
// has actually started consuming).
const SCROLL_ACTIVATE_FRACTION = 0.08;

export default function SampleReadAlongNavigator({ surface = "sample_analysis" }) {
    const navigate = useNavigate();
    const [sectionIds, setSectionIds] = useState([]);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [armed, setArmed] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    const shownRef = useRef(false);

    // Discover sections after mount and observe their visibility.
    useEffect(() => {
        if (typeof document === "undefined") return undefined;
        // Wait one tick so ReportFull has time to render.
        const t = setTimeout(() => {
            const nodes = Array.from(
                document.querySelectorAll("[data-toc-section]"),
            );
            const ids = nodes
                .map((n) => n.getAttribute("data-toc-section"))
                .filter(Boolean);
            setSectionIds(ids);
        }, 250);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        if (sectionIds.length === 0) return undefined;
        if (typeof window === "undefined" || !window.IntersectionObserver)
            return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort(
                        (a, b) =>
                            b.intersectionRatio - a.intersectionRatio,
                    );
                if (visible.length === 0) return;
                const id = visible[0].target.getAttribute("data-toc-section");
                const idx = sectionIds.indexOf(id);
                if (idx >= 0) setActiveIndex(idx);
            },
            { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.1, 0.3, 0.6] },
        );
        sectionIds.forEach((id) => {
            const el = document.querySelector(`[data-toc-section="${id}"]`);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, [sectionIds]);

    // Arm the chip only after the visitor has scrolled meaningfully.
    useEffect(() => {
        const onScroll = () => {
            const doc = document.documentElement;
            const max = doc.scrollHeight - doc.clientHeight;
            if (max <= 0) return;
            if (window.scrollY / max >= SCROLL_ACTIVATE_FRACTION) {
                setArmed(true);
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const total = sectionIds.length;
    const isLast = activeIndex >= 0 && activeIndex === total - 1;
    const progressFraction = total > 0 ? (activeIndex + 1) / total : 0;
    const showAsUploadCta = progressFraction >= UPLOAD_CTA_THRESHOLD;

    const visible =
        !dismissed &&
        armed &&
        total >= 3 &&
        activeIndex >= 0 &&
        !(isLast && !showAsUploadCta);

    // One-shot "shown" telemetry.
    useEffect(() => {
        if (visible && !shownRef.current) {
            shownRef.current = true;
            track("sample_navigator_shown", { surface });
        }
    }, [visible, surface]);

    const nextId = useMemo(() => {
        if (activeIndex < 0 || activeIndex >= total - 1) return null;
        return sectionIds[activeIndex + 1];
    }, [activeIndex, sectionIds, total]);

    const goToNext = () => {
        if (!nextId) return;
        const el = document.querySelector(`[data-toc-section="${nextId}"]`);
        if (!el) return;
        const fromId = sectionIds[activeIndex];
        track("sample_navigator_section_clicked", {
            surface,
            from: fromId,
            to: nextId,
            from_index: activeIndex,
            to_index: activeIndex + 1,
        });
        const y =
            el.getBoundingClientRect().top + window.scrollY - 96; // navbar offset
        window.scrollTo({ top: y, behavior: "smooth" });
    };

    const goToUpload = () => {
        track("sample_navigator_upload_cta_clicked", {
            surface,
            from: sectionIds[activeIndex] || null,
            from_index: activeIndex,
            sections_total: total,
        });
        navigate("/upload");
    };

    if (!visible) return null;

    return (
        <div
            className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 max-w-[calc(100vw-2rem)] sm:max-w-md"
            data-testid="sample-navigator"
            role="complementary"
            aria-label={
                showAsUploadCta
                    ? "Upload your inspection report"
                    : "Read-along navigator"
            }
        >
            {showAsUploadCta ? (
                <div
                    className="bl-card p-3 sm:p-3.5 flex items-center gap-3 shadow-lg border-bl-primary/30 bg-bl-surface/95 backdrop-blur"
                    data-testid="sample-navigator-upload-card"
                >
                    <div className="w-9 h-9 rounded-lg bg-bl-primary text-bl-surface flex items-center justify-center shrink-0">
                        <FileUp className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted">
                            You've read{" "}
                            {Math.round(progressFraction * 100)}% of the sample
                        </div>
                        <div className="font-display font-medium text-bl text-sm leading-snug">
                            Want one like this for{" "}
                            <span className="font-italic-accent text-bl-primary">
                                your inspection?
                            </span>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={goToUpload}
                        className="bl-btn-primary !px-4 !py-2.5 !text-sm shrink-0"
                        data-testid="sample-navigator-upload-btn"
                    >
                        Upload yours
                        <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setDismissed(true)}
                        aria-label="Dismiss navigator"
                        className="w-7 h-7 rounded-full hover:bg-bl-bg flex items-center justify-center text-bl-muted hover:text-bl shrink-0"
                        data-testid="sample-navigator-dismiss-btn"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    onClick={goToNext}
                    className="bl-card group inline-flex items-center gap-3 px-3.5 py-2.5 sm:py-3 shadow-lg bg-bl-surface/95 backdrop-blur hover:border-bl-primary transition-colors w-full text-left"
                    data-testid="sample-navigator-next-btn"
                >
                    <div className="w-8 h-8 rounded-lg bg-bl-teal-tint text-bl-primary flex items-center justify-center shrink-0">
                        <BookOpen className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] tracking-[0.18em] uppercase text-bl-muted font-mono-bl">
                            Section{" "}
                            <span className="text-bl">{activeIndex + 1}</span>{" "}
                            of <span className="text-bl">{total}</span>
                        </div>
                        <div
                            className="font-display font-medium text-bl text-sm leading-snug truncate"
                            data-testid="sample-navigator-next-label"
                        >
                            Up next:{" "}
                            <span className="text-bl-primary">
                                {prettyLabel(nextId)}
                            </span>
                        </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-bl-primary group-hover:translate-x-0.5 transition-transform shrink-0" />
                </button>
            )}
        </div>
    );
}
